import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useActionTasks, TaskStatus, TaskPriority } from '@/hooks/useActionTasks';
import { UserMenu } from '@/components/UserMenu';
import { WorkspaceAvatar } from '@/components/WorkspaceAvatar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import quentariLogo from '@/assets/quentari-logo.svg';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, isPast, format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Loader2,
  ArrowLeft,
  ListTodo,
  CheckCircle2,
  Clock,
  CircleDot,
  Ban,
  AlertCircle,
  Filter,
  Target,
  TrendingUp,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: React.ElementType; color: string }> = {
  open: { label: 'Offen', icon: CircleDot, color: 'text-muted-foreground' },
  in_progress: { label: 'In Bearbeitung', icon: Clock, color: 'text-blue-600 dark:text-blue-400' },
  done: { label: 'Erledigt', icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  blocked: { label: 'Blockiert', icon: Ban, color: 'text-orange-600 dark:text-orange-400' },
  cancelled: { label: 'Abgebrochen', icon: Ban, color: 'text-muted-foreground/60' },
};

const PRIORITY_STYLES: Record<string, string> = {
  critical: 'text-destructive border-destructive/30 bg-destructive/10',
  high: 'text-orange-600 dark:text-orange-400 border-orange-300/30 bg-orange-500/10',
  medium: 'text-muted-foreground border-border bg-muted/30',
  low: 'text-muted-foreground/60 border-border/50 bg-muted/20',
};

const ActionTasks = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { workspace, loading: workspaceLoading } = useWorkspace();
  const { tasks, isLoading, updateTask } = useActionTasks(workspace?.id);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'all'>('all');

  if (authLoading || workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const filteredTasks = tasks.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    return true;
  });

  // Group by session
  const grouped = filteredTasks.reduce((acc, task) => {
    const key = task.session_id;
    const label = task.session?.summary || task.session?.decision_type || 'Unbekannte Session';
    if (!acc[key]) acc[key] = { label, tasks: [] };
    acc[key].tasks.push(task);
    return acc;
  }, {} as Record<string, { label: string; tasks: typeof filteredTasks }>);

  // Stats
  const totalCount = tasks.length;
  const doneCount = tasks.filter(t => t.status === 'done').length;
  const openCount = tasks.filter(t => t.status === 'open' || t.status === 'in_progress').length;
  const overdueCount = tasks.filter(t => t.due_date && isPast(new Date(t.due_date)) && t.status !== 'done' && t.status !== 'cancelled').length;
  const completionRate = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const statusCounts = tasks.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleToggleDone = (task: typeof tasks[0]) => {
    updateTask({ id: task.id, status: task.status === 'done' ? 'open' : 'done' });
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3">
            <img
              src={quentariLogo}
              alt="Quentari"
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105"
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border" />
            <WorkspaceAvatar size="sm" showName />
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="container max-w-7xl mx-auto px-4 md:px-6 py-8 relative z-10">
        <div className="mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-4 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zum Dashboard
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <ListTodo className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Action Tasks</h1>
          </div>
          <p className="text-muted-foreground">
            Alle Aufgaben aus deinen Entscheidungsprozessen
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{totalCount}</p>
                  <p className="text-xs text-muted-foreground">Gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{doneCount}</p>
                  <p className="text-xs text-muted-foreground">Erledigt</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{completionRate}%</p>
                  <p className="text-xs text-muted-foreground">Abgeschlossen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{overdueCount}</p>
                  <p className="text-xs text-muted-foreground">Überfällig</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Fortschritt</span>
              <span className="font-medium">{doneCount} von {totalCount}</span>
            </div>
            <Progress value={completionRate} className="h-2" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs">
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Status: {statusFilter === 'all' ? 'Alle' : STATUS_CONFIG[statusFilter].label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setStatusFilter('all')}>Alle ({totalCount})</DropdownMenuItem>
              {(Object.entries(STATUS_CONFIG) as [TaskStatus, typeof STATUS_CONFIG['open']][]).map(([status, config]) => (
                <DropdownMenuItem key={status} onClick={() => setStatusFilter(status)}>
                  <config.icon className={cn('h-4 w-4 mr-2', config.color)} />
                  {config.label} ({statusCounts[status] || 0})
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs">
                Priorität: {priorityFilter === 'all' ? 'Alle' : priorityFilter}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setPriorityFilter('all')}>Alle</DropdownMenuItem>
              {(['critical', 'high', 'medium', 'low'] as TaskPriority[]).map(p => (
                <DropdownMenuItem key={p} onClick={() => setPriorityFilter(p)}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tasks grouped by session */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="skeleton-shimmer h-20 rounded-xl" />)}
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ListTodo className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {tasks.length === 0
                  ? 'Noch keine Tasks vorhanden. Tasks können aus dem Report importiert werden.'
                  : 'Keine Tasks mit diesem Filter gefunden.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([sessionId, group]) => (
              <div key={sessionId}>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  {group.label}
                  <Badge variant="outline" className="text-[10px]">{group.tasks.length}</Badge>
                </h3>
                <div className="space-y-2">
                  {group.tasks.map(task => {
                    const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done' && task.status !== 'cancelled';
                    const StatusIcon = STATUS_CONFIG[task.status]?.icon || CircleDot;
                    const statusConf = STATUS_CONFIG[task.status];
                    return (
                      <Card
                        key={task.id}
                        className={cn(
                          'transition-all hover:shadow-md',
                          isOverdue && 'border-destructive/30'
                        )}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={task.status === 'done'}
                              onCheckedChange={() => handleToggleDone(task)}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <p className={cn(
                                'text-sm font-medium',
                                task.status === 'done' && 'line-through text-muted-foreground'
                              )}>
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {task.description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', statusConf?.color)}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {statusConf?.label}
                                </Badge>
                                <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', PRIORITY_STYLES[task.priority])}>
                                  {task.priority}
                                </Badge>
                                {task.category && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                                    {task.category}
                                  </Badge>
                                )}
                                {task.due_date && (
                                  <span className={cn('text-[10px]', isOverdue ? 'text-destructive font-medium' : 'text-muted-foreground')}>
                                    {isOverdue && <AlertCircle className="inline h-3 w-3 mr-0.5" />}
                                    Fällig: {format(new Date(task.due_date), 'dd.MM.yyyy', { locale: de })}
                                  </span>
                                )}
                                {task.assignee?.name && (
                                  <span className="text-[10px] text-muted-foreground">
                                    → {task.assignee.name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ActionTasks;
